// src/components/Layout.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.tsx";
import { MultiplayerStatus } from "./MultiplayerStatusBar.tsx";
import { Footer } from "./Footer.tsx";
import { BugReportModal } from "./modals/BugReportModal.tsx";
import { TestRunnerModal } from "./modals/TestRunnerModal.tsx";
import { SurveyModal } from "./modals/SurveyModal.tsx";
import { NotifyFriendModal } from "./modals/NotifyFriendModal.tsx";
import io from "socket.io-client";
import "./Layout.css";

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Bug report modal state
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);

  // Test runner modal state
  const [isTestRunnerOpen, setIsTestRunnerOpen] = useState(false);

  // Survey modal state
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);

  // Notify friend modal state
  const [isNotifyFriendOpen, setIsNotifyFriendOpen] = useState(false);

  // Socket connection for multiplayer status
  const [socket, setSocket] = useState<any>(null);

  // Initialize socket when user logs in
  useEffect(() => {
    if (user?.userId && user?.username) {
      const SERVER_URL = process.env.REACT_APP_API_URL || "";
      
      const newSocket = io(SERVER_URL, {
        auth: {
          userId: user.userId,
          username: user.username,
        },
        reconnection: true,
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      // User logged out, close socket
      if (socket) {
        socket.close();
        setSocket(null);
      }
    }
  }, [user?.userId, user?.username]);

  // Update date/time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDateTime = (date: Date) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };

    const dateStr = date.toLocaleDateString("en-US", dateOptions);
    const timeStr = date.toLocaleTimeString("en-US", timeOptions);

    return { dateStr, timeStr };
  };

  const { dateStr, timeStr } = formatDateTime(currentDateTime);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Navigation tabs configuration - ALL STANDALONE ROUTES
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: "📊", path: "/dashboard" },
    { id: "play", label: "Play", icon: "🎮", path: "/play-online" },
    { id: "multiplayer", label: "Multiplayer", icon: "🌐", path: "/multiplayer" },
    { id: "catalog", label: "Pieces", icon: "📚", path: "/piece-catalog" },
    { id: "variants", label: "Variants", icon: "🎨", path: "/variant-creator" },
    { id: "ai", label: "AI Settings", icon: "🤖", path: "/ai-settings" },
    { id: "leaderboard", label: "Leaderboard", icon: "🏆", path: "/leaderboard" },
    { id: "profile", label: "Profile", icon: "👤", path: `/profile/${user?.userId}` },
    { id: "documents", label: "Documents", icon: "📄", path: "/documents" },
    { id: "help", label: "Help", icon: "❓", path: "/help" },
  ];

  // Simple active tab detection - just match the pathname
  const isActiveTab = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="app-layout">
      {/* Compact Header */}
      <header className="app-header">
        <div className="header-top">
          {/* Logo and Title Section */}
          <div className="header-brand">
            <img
              src="/ExpandedChesslogo001.png"
              alt="Expanded Chess Logo"
              className="app-logo"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
              }}
            />
            <h1 className="app-title">EXPANDED CHESS</h1>
          </div>

          {/* Date/Time and User Section */}
          <div className="header-info">
            <div className="datetime-display">
              <div className="date-text">{dateStr}</div>
              <div className="time-text">{timeStr}</div>
            </div>

            {/* Simple Multiplayer Status Dropdown */}
            {user && socket && (
              <MultiplayerStatus
                socket={socket}
                currentUser={{
                  id: user.userId,
                  username: user.username,
                }}
              />
            )}

            {/* Test Runner Button - Beta Testing */}
            <button
              className="test-runner-btn"
              onClick={() => setIsTestRunnerOpen(true)}
              title="Run a Test"
            >
              <span className="test-icon">&#9745;</span>
              <span className="test-label">Run Test</span>
            </button>

            {/* Bug Report Button - Beta Testing */}
            <button
              className="bug-report-btn"
              onClick={() => setIsBugReportOpen(true)}
              title="Report a Bug"
            >
              <span className="bug-icon">&#128027;</span>
              <span className="bug-label">Report Bug</span>
            </button>

            {/* Survey Button - Beta Testing */}
            <button
              className="survey-btn"
              onClick={() => setIsSurveyOpen(true)}
              title="Take a Survey"
            >
              <span className="survey-icon">&#128203;</span>
              <span className="survey-label">Take Survey</span>
            </button>

            {/* Notify Friend Button - Beta Testing */}
            <button
              className="notify-friend-btn"
              onClick={() => setIsNotifyFriendOpen(true)}
              title="Invite a Friend"
            >
              <span className="notify-icon">&#128140;</span>
              <span className="notify-label">Notify Friend</span>
            </button>

            {user && (
              <div className="user-section">
                <span className="user-welcome">
                  <span className="user-icon">👋</span>
                  {user.username}
                </span>
                <button onClick={handleLogout} className="logout-btn-compact">
                  <span>Logout</span>
                  <span className="logout-icon">➤</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <nav className="header-nav">
          <div className="nav-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={`nav-tab ${isActiveTab(tab.path) ? "active" : ""}`}
                title={tab.label}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="app-main">{children}</main>

      {/* Footer */}
      <Footer />

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={isBugReportOpen}
        onClose={() => setIsBugReportOpen(false)}
      />

      {/* Test Runner Modal */}
      <TestRunnerModal
        isOpen={isTestRunnerOpen}
        onClose={() => setIsTestRunnerOpen(false)}
      />

      {/* Survey Modal */}
      <SurveyModal
        isOpen={isSurveyOpen}
        onClose={() => setIsSurveyOpen(false)}
        pageName={location.pathname}
        pageUrl={location.pathname}
      />

      {/* Notify Friend Modal */}
      <NotifyFriendModal
        isOpen={isNotifyFriendOpen}
        onClose={() => setIsNotifyFriendOpen(false)}
      />
    </div>
  );
};

export default Layout;
